import React, { useEffect, useRef, useState, createContext, useContext } from 'react';
import BubbleButton from './BubbleButton';
import './BubbleDrawer.scss';

export type BubbleDrawerPosition = 'left' | 'right';

interface BubbleDrawerPropsBase {
  hideTools?: boolean;
  className?: string;
  containerClassName?: string;
  onClose?: () => void;
}

export type BubbleDrawerProps = React.HTMLAttributes<HTMLDivElement> & BubbleDrawerPropsBase;

// Context for disabling scroll
export const BubbleDrawerScrollContext = createContext<{
  scrollEnabled: boolean;
  toggleScroll: (newState: boolean) => void;
  scrollToBottom: () => void;
}>({
  scrollEnabled: true,
  toggleScroll: () => {},
  scrollToBottom: () => {},
});

export function useBubbleDrawerScroll() {
  return useContext(BubbleDrawerScrollContext);
}

export default function BubbleDrawer(props: BubbleDrawerProps) {
  const {
    hideTools,
    containerClassName: propContainerClasses,
    onClose,
    className: propClasses,
    ...rest
  } = props;

  const [position, setPosition] = useState<BubbleDrawerPosition>('right');
  const [scrollEnabled, setScrollEnabled] = useState(true);
  const drawerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const classes = ['bubble-drawer'];
  if (propClasses) classes.push(propClasses);

  const containerClasses = ['drawer-container'];
  if (propContainerClasses) containerClasses.push(propContainerClasses);

  let containerStyle: React.CSSProperties = {};
  if (position === 'left') {
    containerStyle.left = 0;
  } else {
    containerStyle.right = 0;
  }

  useEffect(() => {
    if (drawerRef.current) drawerRef.current.focus();
  }, [drawerRef]);

  useEffect(() => {
    if (!containerRef.current) return;

    if (scrollEnabled) {
      containerRef.current.style.overflowY = 'auto';
    } else {
      containerRef.current.scrollTo({ top: 0 });
      containerRef.current.style.overflowY = 'hidden';
    }
  }, [scrollEnabled]);

  function scrollToBottom() {
    setTimeout(() => {
      containerRef.current?.scrollTo({ top: 99999, behavior: 'smooth' });
    }, 0);
  }

  return (
    <BubbleDrawerScrollContext.Provider
      value={{ scrollEnabled, toggleScroll: setScrollEnabled, scrollToBottom }}
    >
      <div {...rest} className={classes.join(' ')}>
        <div
          className="drawer-backdrop"
          onClick={props.onClose}
          tabIndex={0}
          ref={drawerRef}
          onKeyDown={e => {
            if (e.key === 'Escape' && onClose) {
              e.stopPropagation();
              onClose();
            }
          }}
        >
          {!props.hideTools && (
            <div className={`p-3 ${position === 'left' ? 'text-right' : 'text-left'}`}>
              <BubbleButton
                variant="white"
                icon="arrows-left-right"
                onClick={() => {
                  setPosition(position === 'left' ? 'right' : 'left');
                }}
              />
            </div>
          )}
        </div>
        <div ref={containerRef} className={containerClasses.join(' ')} style={containerStyle}>
          {props.children}
        </div>
      </div>
    </BubbleDrawerScrollContext.Provider>
  );
}
